import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from 'semantic-ui-react';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { featureToggleUtil, accessUtil } from '../../utils';
import { shouldOpenAudiobookTeaser } from '../../utils/audiobookBetaTeaser';
import AudiobookBetaSignup from '../../components/audiobook/audiobookBetaSignup';
import AudiobookGenerationPanel from '../../components/audiobook/audiobookGenerationPanel';
import AudiobookVoiceSamplePanel from '../../components/audiobook/audiobookVoiceSamplePanel';
import { recordAudiobookTabOpen } from '../../utils/audiobookTabAnalytics';

/**
 * The author Audiobook panel (docs/features/audiobooks/beta-teaser.md + author UX gap).
 * One book-level surface with three states:
 *
 *  - NO access     → the beta-signup teaser (per-book price + "Request beta access").
 *  - CONTROLLED access → voice selection, optional sample, then self-funded production.
 *  - FULL only     → the generation panel (cost + Generate + preview/listen).
 *
 * Controlled access requires the global kill switch and the individual entitlement;
 * full access is the `audiobook` global flag OR the per-user grant.
 * `?audiobookTeaser=1` forces the teaser even for access-holders (QA preview) —
 * see utils/audiobookBetaTeaser.
 */

const Wrapper = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 20px 48px;
`;

const BookAudiobook: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentBook = useSelector((state: any) => state.book.currentBook);
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const recordedBook = useRef<string | null>(null);
  const bookId = currentBook?._id;
  const mayEdit = Boolean(currentBook && accessUtil.isAllowed(currentBook.access, ['edit']));
  useEffect(() => {
    if (!flagsLoading && mayEdit && bookId && recordedBook.current !== bookId) {
      recordedBook.current = bookId;
      recordAudiobookTabOpen(bookId).catch(() => undefined);
    }
  }, [bookId, flagsLoading, mayEdit]);

  if (!currentBook) return null;

  // Book-level pages are author/edit surfaces.
  if (!accessUtil.isAllowed(currentBook?.access, ['edit'])) {
    return (
      <Wrapper>
        <p style={{ color: 'var(--br-muted)' }}>
          {t('AudiobookNoAccess', 'Only the author can manage the audiobook.')}
        </p>
      </Wrapper>
    );
  }

  // Wait for the global-flags fetch before deciding teaser-vs-panel. Deciding on
  // the pre-load render (flags undefined ⇒ audiobook=false) would flash the teaser
  // AND fire a spurious A/B impression for global-flag-only access-holders.
  if (flagsLoading) {
    return (
      <Wrapper>
        <Loader active inline="centered" style={{ marginTop: 40 }} />
      </Wrapper>
    );
  }

  const fullAudiobookEnabled =
    flags?.audiobook === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'audiobook');
  const sampleAccess =
    flags?.audiobook_voice_samples === true &&
    featureToggleUtil.hasFeatureAccess(userProfile, 'audiobook_voice_samples');

  const forceTeaser = shouldOpenAudiobookTeaser(true, location.search);

  const contentVersionId = currentBook?.content?.at(-1)?.id;
  const currentAuthorId = currentBook?.author?._id || currentBook?.author;
  const canManageReaderAccess = Boolean(
    currentAuthorId &&
      userProfile?._id &&
      String(currentAuthorId) === String(userProfile._id)
  );

  const signup = (
    <AudiobookBetaSignup
      bookId={currentBook._id}
      bookTitle={currentBook.title}
      // access-holder viewing the teaser = a QA preview; don't log an A/B impression.
      preview={fullAudiobookEnabled || sampleAccess}
    />
  );

  return (
    <Wrapper>
      {forceTeaser ? (
        signup
      ) : sampleAccess ? (
        <AudiobookVoiceSamplePanel
          bookId={currentBook._id}
          bookTitle={currentBook.title}
        />
      ) : (
        <AudiobookGenerationPanel
          bookId={currentBook._id}
          contentVersionId={contentVersionId}
          bookTitle={currentBook.title}
          canManageReaderAccess={canManageReaderAccess}
          generationEnabled={fullAudiobookEnabled}
          unavailableFallback={signup}
        />
      )}
    </Wrapper>
  );
};

export default BookAudiobook;
